// YNAB HTTP service wrapper - lightweight, SDK-free implementation that talks to the
// official YNAB REST API. Designed for browser & test environments (MSW).
// Responsibilities:
// - Manage access token (PAT) used for Authorization header
// - Expose simple methods: setAccessToken, clearAccessToken, getBudgets, getAccounts, getTransactions
// - Normalize and filter transactions (cleared/reconciled, outflows only, skip zero/future/malformed)

const API_BASE = "https://api.youneedabudget.com/v1";
let accessToken: string | null = null;

// Debug: identify the module instance to detect duplicate imports/instances
const __ynabServiceInstanceId = Math.random().toString(36).slice(2, 8);
// Lightweight, non-sensitive module load log
console.debug(
  `[ynabService#${__ynabServiceInstanceId}] module loaded (tokenPresent=${!!accessToken})`,
);

// Subscribers notified whenever token presence changes (no secrets exposed)
const tokenListeners = new Set<(present: boolean) => void>();
function notifyTokenChange() {
  const present = !!accessToken;
  for (const l of tokenListeners) {
    try {
      l(present);
    } catch {
      // ignore listener errors
    }
  }
}

export function onAccessTokenChange(listener: (present: boolean) => void) {
  tokenListeners.add(listener);
  // Immediately notify current state to new subscribers
  try {
    listener(!!accessToken);
  } catch {
    // ignore
  }
  return () => tokenListeners.delete(listener);
}

export type YNABBudget = {
  id: string;
  name: string;
  // kept intentionally minimal for UI usage
};

export type YNABAccount = {
  id: string;
  name: string;
  currency: string | null;
  // other fields are ignored for now
};

export type YNABTransaction = {
  id: string;
  date: string; // YYYY-MM-DD
  amount: number; // decimal currency e.g. 12.34 (converted from milliunits)
  memo?: string;
  payee_name?: string;
  category_name?: string;
  cleared?: "uncleared" | "cleared" | "reconciled" | string;
};

export function setAccessToken(token: string) {
  accessToken = token;
  // Do not log the token value; log only its presence/length
  console.debug(
    `[ynabService#${__ynabServiceInstanceId}] setAccessToken(len=${token?.length ?? 0})`,
  );
  notifyTokenChange();
}

export function clearAccessToken() {
  accessToken = null;
  console.debug(`[ynabService#${__ynabServiceInstanceId}] clearAccessToken()`);
  notifyTokenChange();
}

export function hasAccessToken(): boolean {
  const present = !!accessToken;
  console.debug(
    `[ynabService#${__ynabServiceInstanceId}] hasAccessToken() -> ${present}`,
  );
  return present;
}

function ensureAuth() {
  if (!accessToken) {
    throw new Error("YNAB access token is not configured");
  }
}

function authHeaders() {
  return {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };
}

// Small fetch wrapper with retry/backoff for transient errors (429, network).
// Keeps behavior predictable for UI callers by returning a Response or throwing
// a normalized error. Tuned for browser/test environments.
async function fetchWithRetry(
  url: string,
  init: RequestInit = {},
  retries = 3,
  baseBackoffMs = 300,
): Promise<Response> {
  let lastErr: any = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url, init);

      // If rate limited, wait and retry (respect Retry-After if present)
      if (res.status === 429 && attempt < retries) {
        const ra = res.headers.get ? res.headers.get("Retry-After") : null;
        const waitMs = ra
          ? Number(ra) * 1000
          : baseBackoffMs * Math.pow(2, attempt);
        await new Promise((r) => setTimeout(r, waitMs));
        continue;
      }

      return res;
    } catch (e) {
      lastErr = e;
      // Network failure - retry with exponential backoff if attempts remain
      if (attempt < retries) {
        const waitMs = baseBackoffMs * Math.pow(2, attempt);

        await new Promise((r) => setTimeout(r, waitMs));
        continue;
      }
      throw e;
    }
  }
  // If we get here, all retries exhausted
  throw lastErr ?? new Error("Failed to fetch");
}

async function handleResponse(res: Response) {
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    // ignore JSON parse failures
  }
  if (!res.ok) {
    // Construct normalized error for callers to inspect
    const err: any = new Error(`YNAB API error (${res.status})`);
    err.status = res.status;
    err.body = body;
    // Mark auth errors so callers can react (clear token / prompt)
    if (res.status === 401 || res.status === 403) {
      err.auth = true;
    }
    // Expose retry-after for rate-limited responses when available
    if (res.status === 429) {
      try {
        const ra = res.headers.get ? res.headers.get("Retry-After") : null;
        if (ra) err.retryAfter = Number(ra);
      } catch {
        // ignore
      }
    }
    throw err;
  }
  return body;
}

/**
 * Fetch budgets for the authenticated user
 */
export async function getBudgets(): Promise<YNABBudget[]> {
  ensureAuth();
  const res = await fetchWithRetry(`${API_BASE}/budgets`, {
    method: "GET",
    headers: authHeaders(),
  });
  const body = await handleResponse(res);
  return (body?.data?.budgets ?? []).map((b: any) => ({
    id: b.id,
    name: b.name,
  }));
}

/**
 * Fetch accounts for a given budget
 */
export async function getAccounts(budgetId: string): Promise<YNABAccount[]> {
  ensureAuth();
  const res = await fetchWithRetry(
    `${API_BASE}/budgets/${encodeURIComponent(budgetId)}/accounts`,
    {
      method: "GET",
      headers: authHeaders(),
    },
  );
  const body = await handleResponse(res);
  return (body?.data?.accounts ?? []).map((a: any) => ({
    id: a.id,
    name: a.name,
    currency: a.currency ?? null,
  }));
}

/**
 * Fetch transactions for a given budget+account.
 * The function normalizes and filters transactions:
 * - include only cleared OR reconciled transactions
 * - include only outflows (amount < 0 in milliunits)
 * - exclude zero amounts, malformed entries, and future-dated transactions
 */
export async function getTransactions(
  budgetId: string,
  accountId: string,
  sinceDate?: string,
): Promise<YNABTransaction[]> {
  ensureAuth();
  let url = `${API_BASE}/budgets/${encodeURIComponent(budgetId)}/accounts/${encodeURIComponent(
    accountId,
  )}/transactions`;
  if (sinceDate) {
    url += `?since_date=${encodeURIComponent(sinceDate)}`;
  }

  const res = await fetchWithRetry(url, {
    method: "GET",
    headers: authHeaders(),
  });
  const body = await handleResponse(res);
  const raw: any[] = body?.data?.transactions ?? [];
  return normalizeAndFilterTransactions(raw);
}

/**
 * Search transactions for a given budget+account with filter text.
 * This applies client-side filtering to reduce API calls while maintaining search functionality.
 * @param budgetId - YNAB budget ID
 * @param accountId - YNAB account ID
 * @param searchQuery1 - First search term to filter by memo, payee name, category, date, or amount
 * @param searchQuery2 - Optional second search term for AND filtering
 * @param sinceDate - Optional date to limit transaction scope
 */
export async function searchTransactions(
  budgetId: string,
  accountId: string,
  searchQuery1: string,
  searchQuery2?: string,
  sinceDate?: string,
): Promise<YNABTransaction[]> {
  // Get all transactions first
  const transactions = await getTransactions(budgetId, accountId, sinceDate);

  // Normalize search queries
  const query1 = searchQuery1?.trim().toLowerCase() || "";
  const query2 = searchQuery2?.trim().toLowerCase() || "";

  // Apply client-side filtering
  if (!query1 && !query2) {
    return [];
  }

  return transactions.filter((t) => {
    const transactionText = [
      t.memo ?? "",
      t.payee_name ?? "",
      t.category_name ?? "",
      t.date,
      Math.abs(t.amount).toFixed(2),
    ]
      .join(" ")
      .toLowerCase();

    // Check if transaction matches query1 (if provided)
    const matchesQuery1 = !query1 || transactionText.includes(query1);

    // Check if transaction matches query2 (if provided)
    const matchesQuery2 = !query2 || transactionText.includes(query2);

    // Return true only if both conditions are met (AND logic)
    return matchesQuery1 && matchesQuery2;
  });
}

function normalizeAndFilterTransactions(raw: any[]): YNABTransaction[] {
  const out: YNABTransaction[] = [];
  const today = new Date().toISOString().split("T")[0];
  for (const t of raw) {
    try {
      // Basic validation
      if (!t || typeof t.id !== "string" || t.id.trim() === "") continue;
      const status = (t.cleared ?? "").toString();
      // Include only cleared or reconciled; exclude uncleared
      if (status === "uncleared") continue;
      // Amount must be present and numeric (YNAB uses milliunits as integer)
      const milli =
        typeof t.amount === "number" ? t.amount : parseInt(t.amount, 10);
      if (!isFinite(milli)) continue;
      if (milli === 0) continue;
      // Only outflows (negative milliunits)
      if (!(milli < 0)) continue;
      // Date validation
      const date = t.date;
      if (!date || typeof date !== "string") continue;
      if (date > today) continue; // future-dated transaction - skip
      // All checks passed; normalize
      out.push({
        id: t.id,
        date,
        amount: convertMilliunits(milli),
        memo: t.memo ?? "",
        payee_name: t.payee_name ?? "",
        category_name: t.category_name ?? "",
        cleared: status as any,
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      // Skip malformed transaction silently (per ADR)
      continue;
    }
  }
  return out;
}

function convertMilliunits(milli: number): number {
  // YNAB milliunits: 1000 milliunits = 1.00 currency
  return milli / 1000;
}

/**
 * Test YNAB connection with a given token
 */
export async function testYnabConnection(token: string): Promise<void> {
  const originalToken = accessToken;
  try {
    setAccessToken(token);
    // Try to fetch budgets as a simple connectivity test
    await getBudgets();
  } finally {
    // Restore original token
    if (originalToken) {
      setAccessToken(originalToken);
    } else {
      clearAccessToken();
    }
  }
}

// Utility: expose a small helper for tests / callers to detect duplicate candidates
export function transactionKeyCandidate(t: YNABTransaction) {
  // Primary: ynab transaction id; Secondary: date + amount
  return `${t.id}::${t.date}::${t.amount}`;
}
